// Deduplication ids shared between a browser pixel and its server-side twin
// (Meta Conversions API, TikTok Events API).
//
// Both channels report the same signup. The ad platform collapses them only
// when the two carry an identical event_id, so the id is minted before the
// signup request, travels to the backend with it, and is read back when the
// pixel fires.
//
// sessionStorage, not a module variable: the Google flow leaves the page for
// Google's consent screen and comes back to a fresh JS context, which would
// lose an in-memory value and break deduplication on exactly that path.

export const randomEventId = (): string => {
  // crypto.randomUUID needs a secure context; fall back rather than throw, an
  // id that is merely unique-enough still deduplicates correctly.
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }

  return `ev-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`
}

/** One id slot per platform, so each pixel consumes only its own id. */
export const createConversionEventId = (storageKey: string) => ({
  /** Mints the id for a signup attempt and remembers it for the pixel call. */
  mint: (): string => {
    const id = randomEventId()
    try {
      sessionStorage.setItem(storageKey, id)
    } catch {
      // Private mode / storage disabled: the id still reaches the backend,
      // only the browser half loses deduplication. Better than failing the
      // signup.
    }

    return id
  },

  /**
   * Returns the id minted for the signup in flight and clears it, so a later
   * conversion never reuses a stale one.
   */
  consume: (): string | undefined => {
    try {
      const stored = sessionStorage.getItem(storageKey)?.trim()
      sessionStorage.removeItem(storageKey)

      return stored ? stored : undefined
    } catch {
      return undefined
    }
  }
})
